import { useEffect, useRef, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL || "";
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const LOCAL_FALLBACK_KEY = "unixorn_the_list";

const isValidEmail = (value) => EMAIL_REGEX.test(value);

const persistLocally = (email) => {
  try {
    const existing = JSON.parse(localStorage.getItem(LOCAL_FALLBACK_KEY) || "[]");
    existing.push({ email, ts: new Date().toISOString() });
    localStorage.setItem(LOCAL_FALLBACK_KEY, JSON.stringify(existing));
  } catch (err) {
    // localStorage can throw (private mode, quota); surface it for debugging
    // but never block the success flow on a fallback failure.
    console.error("TheList localStorage fallback failed:", err);
  }
};

const submitSubscriber = async (email) => {
  const res = await fetch(`${API}/api/subscribers`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, source: "homepage_the_list" }),
  });
  if (!res.ok) throw new Error(`Request failed: ${res.status}`);
};

/** Animated reveal when section scrolls into view. */
const useRevealOnView = (ref) => {
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setIsVisible(true)),
      { threshold: 0.15 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref]);
  return isVisible;
};

/** Fetch the homepage drop teaser line from the backend config. */
const useDropTeaser = () => {
  const [teaser, setTeaser] = useState("");
  useEffect(() => {
    let active = true;
    fetch(`${API}/api/drop-teaser`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (active && data?.teaser) setTeaser(data.teaser);
      })
      .catch((err) => {
        console.error("TheList drop-teaser fetch failed:", err);
      });
    return () => {
      active = false;
    };
  }, []);
  return teaser;
};

const TheListForm = ({ email, setEmail, status, setStatus, onSubmit }) => (
  <form
    onSubmit={onSubmit}
    className="flex flex-col sm:flex-row items-stretch gap-3 sm:gap-0 max-w-xl mx-auto"
  >
    <input
      type="email"
      required
      value={email}
      onChange={(e) => {
        setEmail(e.target.value);
        if (status === "error") setStatus("idle");
      }}
      placeholder="YOUR EMAIL"
      disabled={status === "submitting"}
      data-testid="the-list-input"
      className="flex-1 bg-transparent border border-[#F4EFE6]/30 sm:border-r-0 text-[#F4EFE6] placeholder:text-[#F4EFE6]/40 placeholder:tracking-[0.25em] placeholder:text-xs h-14 px-5 outline-none focus:border-[#F4EFE6] transition-colors duration-300 uppercase tracking-widest text-sm"
    />
    <button
      type="submit"
      disabled={status === "submitting"}
      data-testid="the-list-submit"
      className="bg-[#F4EFE6] text-black hover:bg-white h-14 px-10 transition-colors duration-300 uppercase tracking-[0.3em] font-semibold text-sm border border-[#F4EFE6] disabled:opacity-60 disabled:cursor-not-allowed"
    >
      {status === "submitting" ? "..." : "JOIN"}
    </button>
  </form>
);

/**
 * THE LIST — minimal, full-width, dark email capture sitting between the
 * featured drops and the footer. Brand-native, no pop-ups, no noise.
 */
export const TheList = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const sectionRef = useRef(null);
  const isVisible = useRevealOnView(sectionRef);
  const dropTeaser = useDropTeaser();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!isValidEmail(trimmed)) {
      setStatus("error");
      setErrorMsg("Enter a valid email.");
      return;
    }
    setStatus("submitting");
    setErrorMsg("");
    try {
      await submitSubscriber(trimmed);
    } catch (err) {
      // Backend unreachable — fall back to local persistence so we never drop a capture.
      console.error("TheList submit to backend failed; using local fallback:", err);
      persistLocally(trimmed);
    }
    setEmail("");
    setStatus("success");
  };

  return (
    <section
      ref={sectionRef}
      id="the-list"
      data-testid="the-list-section"
      className="w-full bg-black text-[#F4EFE6] py-24 md:py-32 px-6 md:px-12"
    >
      <div
        className={`max-w-3xl mx-auto text-center transition-opacity duration-1000 ${
          isVisible ? "opacity-100" : "opacity-0"
        }`}
      >
        <h2
          data-testid="the-list-headline"
          className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-4"
        >
          THE LIST
        </h2>
        {dropTeaser && (
          <p
            data-testid="the-list-drop-teaser"
            className="text-[11px] md:text-xs tracking-[0.4em] uppercase text-[#F4EFE6]/50 mb-5"
          >
            {dropTeaser}
          </p>
        )}
        <p
          data-testid="the-list-subline"
          className="text-base md:text-lg text-[#F4EFE6]/70 tracking-wide mb-12"
        >
          Be first when the next drop lands.
        </p>

        {status === "success" ? (
          <p
            data-testid="the-list-success"
            className="text-sm tracking-[0.3em] uppercase text-[#F4EFE6]/80"
          >
            You&apos;re on the list.
          </p>
        ) : (
          <TheListForm
            email={email}
            setEmail={setEmail}
            status={status}
            setStatus={setStatus}
            onSubmit={handleSubmit}
          />
        )}

        {status === "error" && (
          <p
            data-testid="the-list-error"
            className="mt-4 text-xs tracking-widest uppercase text-[#F4EFE6]/60"
          >
            {errorMsg}
          </p>
        )}
      </div>
    </section>
  );
};

export default TheList;
